import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../data";
import { currentLang, t } from "../../../lib/sprache";
import { fehlerText } from "../../../lib/fehler";
import { date } from "../../../lib/format";
import { preisNettoHinweis } from "../../../model/tarif";
import { billingChange, ibanLesbar, loadSettings, type BillingChangeResult, type RechnungsBank } from "./data";

/**
 * Abo „Auf Rechnung (Überweisung)“ (0590, Inhaber 29.10.2026): dritte Zahlungsart neben Karte und PayPal. Stripe schickt zu jedem
 * Abrechnungszeitraum eine Rechnung per Mail; überwiesen wird binnen 14 Tagen auf das Konto aus den Plattform-Standards, mit der
 * Rechnungsnummer als Verwendungszweck. Firma und Anschrift sind Pflicht (vorbefüllt aus den Firmendaten). Zwei Wege: erster
 * Abschluss (invoice_checkout, ohne Bezahlseite) und laufendes Karten-Abo umstellen (invoice_switch). Ohne Bankverbindung nicht
 * angeboten (rechnungsBank); in der Demo zeigt der Knopf die Meldung „In der Demo gibt es keine Zahlung“.
 */
export const RECHNUNG_ZAHLUNGSZIEL_TAGE = 14;

const LAENDER = ["DE", "AT", "CH", "NL", "BE", "LU", "FR", "IT", "ES", "PL", "DK", "SE", "GB", "US", "AE"];
function landName(code: string): string {
  try {
    return new Intl.DisplayNames([currentLang()], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Bankverbindung, wie sie auf jeder Rechnung steht */
export function BankKasten({ bank, nummer }: { bank: RechnungsBank; nummer?: string | null }) {
  return (
    <dl className="tk-rechnung-bank">
      <div><dt>{t("Kontoinhaber", "Account holder")}</dt><dd>{bank.holder}</dd></div>
      <div><dt>IBAN</dt><dd className="mono">{ibanLesbar(bank.iban)}</dd></div>
      {bank.bic && <div><dt>BIC</dt><dd className="mono">{bank.bic}</dd></div>}
      <div><dt>{t("Verwendungszweck", "Payment reference")}</dt><dd>{nummer ? <span className="mono">{nummer}</span> : t("die Rechnungsnummer", "the invoice number")}</dd></div>
    </dl>
  );
}

export function RechnungAbschluss({ orgId, bank, modus, plan = null, interval = "month", ohneTestphase = false, testBis = null, code, extraSeats = 0, extraTalents = 0, onAbbrechen, onFertig }: {
  orgId: string; bank: RechnungsBank; modus: "abschluss" | "umstellen";
  plan?: string | null; interval?: "month" | "year"; ohneTestphase?: boolean;
  /** Ende der Testphase (Anzeige, wann die erste Rechnung kommt); null = erste Rechnung sofort */
  testBis?: string | null;
  code?: string | null; extraSeats?: number; extraTalents?: number;
  onAbbrechen: () => void;
  onFertig: (r: BillingChangeResult) => void | Promise<void>;
}) {
  const [f, setF] = useState({ name: "", line1: "", postal_code: "", city: "", country: "DE", vat_id: "", email: "" });
  const [falsch, setFalsch] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Vorbefüllt aus den Firmendaten (wie bei PayPal); eigene Eingaben bleiben
  useEffect(() => {
    let aus = false;
    loadSettings(api, orgId).then((s) => {
      if (aus) return;
      setF((alt) => ({
        ...alt,
        name: alt.name || s.legal_name || "", line1: alt.line1 || s.street || "", postal_code: alt.postal_code || s.postal_code || "",
        city: alt.city || s.city || "", country: s.country && LAENDER.includes(s.country) && alt.country === "DE" ? s.country : alt.country, vat_id: alt.vat_id || s.vat_id || "",
      }));
    }).catch(() => undefined);
    return () => { aus = true; };
  }, [orgId]);
  // Sofortige Sperre gegen Doppelklick (K-097)
  const laeuft = useRef(false);
  const feld = (k: keyof typeof f, label: string, opts: { auto?: string; type?: string; placeholder?: string } = {}) => (
    <label>
      {label}
      <input value={f[k]} type={opts.type} autoComplete={opts.auto} placeholder={opts.placeholder} aria-invalid={falsch.includes(k) || undefined}
        onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );
  const los = async () => {
    if (laeuft.current || (modus === "abschluss" && !plan)) return;
    laeuft.current = true;
    setBusy(true);
    setFehler(null);
    setFalsch([]);
    try {
      const r = modus === "abschluss"
        ? await billingChange(api, orgId, { action: "invoice_checkout", plan: plan!, interval, rechnung: f, ...(ohneTestphase ? { ohne_testphase: true } : {}),
          ...(code?.trim() ? { code: code.trim() } : {}), ...(extraSeats ? { extra_seats: extraSeats } : {}), ...(extraTalents ? { extra_talents: extraTalents } : {}) })
        : await billingChange(api, orgId, { action: "invoice_switch", rechnung: f });
      await onFertig(r);
    } catch (e) {
      const felder = e instanceof ApiError && e.hint?.startsWith("billing:fields:") ? e.hint.slice("billing:fields:".length).split(",").filter(Boolean) : undefined;
      if (felder?.length) setFalsch(felder);
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
      laeuft.current = false;
    }
  };
  const erste = modus === "umstellen"
    ? t("Ab der nächsten Abrechnung kommt statt der Abbuchung eine Rechnung per Mail.", "From the next billing period you get an invoice by email instead of a card charge.")
    : testBis
      ? t(`Die erste Rechnung kommt am ${date(testBis)}, zum Ende der Testphase. Bis dahin ist nichts zu zahlen.`, `The first invoice arrives on ${date(testBis)}, at the end of the trial. Nothing is due until then.`)
      : t("Die erste Rechnung kommt gleich per Mail.", "The first invoice arrives by email right away.");
  return (
    <div className="panel stack tk-rechnung-abschluss" aria-labelledby="rechnung-titel">
      <strong id="rechnung-titel">{modus === "umstellen" ? t("Auf Rechnung umstellen", "Switch to invoice") : t("Auf Rechnung zahlen (Überweisung)", "Pay by invoice (bank transfer)")}</strong>
      <small className="tk-rechnung-text">{t(`Zu jedem Abrechnungszeitraum kommt eine Rechnung per Mail. Du überweist sie innerhalb von ${RECHNUNG_ZAHLUNGSZIEL_TAGE} Tagen, mit der Rechnungsnummer als Verwendungszweck.`,
        `For each billing period you receive an invoice by email. You pay it by bank transfer within ${RECHNUNG_ZAHLUNGSZIEL_TAGE} days, with the invoice number as the payment reference.`)} {erste}</small>
      <BankKasten bank={bank} />
      <small>{t("Für die Rechnung brauchen wir Firma und Anschrift.", "For the invoice we need company and address.")} {preisNettoHinweis()}</small>
      <div className="tk-felder">
        {feld("name", t("Firma", "Company"), { auto: "organization" })}
        {feld("line1", t("Straße und Hausnummer", "Street and number"), { auto: "address-line1" })}
        {feld("postal_code", t("PLZ", "Postcode"), { auto: "postal-code" })}
        {feld("city", t("Ort", "City"), { auto: "address-level2" })}
        <label>
          {t("Land", "Country")}
          <select value={f.country} aria-invalid={falsch.includes("country") || undefined} onChange={(e) => setF({ ...f, country: e.target.value })}>
            {LAENDER.map((k) => <option key={k} value={k}>{landName(k)}</option>)}
          </select>
        </label>
        {feld("vat_id", t("USt-IdNr.", "VAT ID"))}
        {feld("email", t("Rechnung per Mail an", "Send invoices to"), { auto: "email", type: "email", placeholder: t("leer: deine Anmelde-Adresse", "empty: your sign-in address") })}
      </div>
      <small>{t(`Bleibt eine Rechnung länger als ${RECHNUNG_ZAHLUNGSZIEL_TAGE} Tage nach der Fälligkeit offen, ist das Konto danach nur noch lesbar, bis die Zahlung eingegangen ist.`,
        `If an invoice stays unpaid for more than ${RECHNUNG_ZAHLUNGSZIEL_TAGE} days after it is due, the account becomes read-only until the payment has arrived.`)}</small>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      <div className="row">
        <button type="button" className="primary" disabled={busy || (modus === "abschluss" && !plan)} aria-busy={busy} onClick={() => void los()}>
          {busy ? t("Einen Moment …", "One moment …")
            : modus === "umstellen" ? t("Auf Rechnung umstellen", "Switch to invoice")
            : testBis ? t("Test auf Rechnung starten", "Start trial with invoice") : t("Auf Rechnung abschließen", "Subscribe with invoice")}
        </button>
        <button type="button" disabled={busy} onClick={onAbbrechen}>{t("Zurück", "Back")}</button>
      </div>
    </div>
  );
}
